import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuthContextInspector = ({ user, userProfile, onEvent }) => {
  const [session, setSession] = useState(null);
  const [authState, setAuthState] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAuthState();
  }, [user, userProfile]);

  const loadAuthState = async () => {
    onEvent?.('Loading auth state', 'info');

    try {
      const { data: { session: currentSession }, error } = await supabase?.auth?.getSession();
      
      if (error) {
        onEvent?.('Failed to load session', 'error', error);
      } else {
        setSession(currentSession);
        
        const state = {
          authenticated: !!currentSession,
          userId: user?.id,
          userEmail: user?.email,
          profileLoaded: !!userProfile,
          profileId: userProfile?.id,
          sessionExpiry: currentSession?.expires_at,
          accessToken: currentSession?.access_token ? 'Present' : 'Missing',
          refreshToken: currentSession?.refresh_token ? 'Present' : 'Missing'
        };
        
        setAuthState(state);
        onEvent?.('Auth state loaded', 'success', state);
      }
    } catch (error) {
      onEvent?.('Auth state load exception', 'error', error);
    }
  };

  const refreshSession = async () => {
    setRefreshing(true);
    onEvent?.('Refreshing session', 'action');

    try {
      const { data, error } = await supabase?.auth?.refreshSession();
      
      if (error) {
        onEvent?.('Session refresh failed', 'error', error);
      } else {
        setSession(data?.session);
        onEvent?.('Session refreshed successfully', 'success', data);
        await loadAuthState();
      }
    } catch (error) {
      onEvent?.('Session refresh exception', 'error', error);
    } finally {
      setRefreshing(false);
    }
  };

  const formatExpiry = (expiryTimestamp) => {
    if (!expiryTimestamp) return 'N/A';
    const expiry = new Date(expiryTimestamp * 1000);
    const now = new Date();
    const diff = expiry - now;
    const minutes = Math.floor(diff / 1000 / 60);
    return `${expiry?.toLocaleString()} (${minutes} min remaining)`;
  };

  const renderValue = (value) => {
    if (value === null) return <span className="text-gray-500 italic">null</span>;
    if (value === undefined) return <span className="text-gray-500 italic">undefined</span>;
    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-400' : 'text-red-400'}>{String(value)}</span>;
    }
    if (typeof value === 'string') {
      return <span className="text-yellow-300">{value}</span>;
    }
    return <span className="text-gray-300">{JSON.stringify(value)}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">AuthContext Inspector</h2>
          <p className="text-sm text-gray-400 mt-1">
            Current authentication state and session details
          </p>
        </div>
        <Button
          variant="outline"
          onClick={refreshSession}
          disabled={refreshing}
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <AppIcon name="RefreshCw" className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Session
        </Button>
      </div>
      {/* Auth State Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`rounded-lg border p-4 ${
          authState?.authenticated
            ? 'bg-green-900/20 border-green-700' :'bg-red-900/20 border-red-700'
        }`}>
          <div className="flex items-center gap-2">
            <AppIcon
              name={authState?.authenticated ? 'CheckCircle' : 'XCircle'}
              className={`w-5 h-5 ${authState?.authenticated ? 'text-green-500' : 'text-red-500'}`}
            />
            <span className="text-sm text-gray-400">Authentication</span>
          </div>
          <div className="text-2xl font-bold text-white mt-2">
            {authState?.authenticated ? 'Active' : 'Inactive'}
          </div>
        </div>

        <div className={`rounded-lg border p-4 ${
          authState?.profileLoaded
            ? 'bg-blue-900/20 border-blue-700' :'bg-yellow-900/20 border-yellow-700'
        }`}>
          <div className="flex items-center gap-2">
            <AppIcon
              name={authState?.profileLoaded ? 'User' : 'UserX'}
              className={`w-5 h-5 ${authState?.profileLoaded ? 'text-blue-500' : 'text-yellow-500'}`}
            />
            <span className="text-sm text-gray-400">Profile</span>
          </div>
          <div className="text-2xl font-bold text-white mt-2">
            {authState?.profileLoaded ? 'Loaded' : 'Not Loaded'}
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AppIcon name="Key" className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-400">Tokens</span>
          </div>
          <div className="text-2xl font-bold text-white mt-2">
            {authState?.accessToken === 'Present' && authState?.refreshToken === 'Present' ?'Valid' :'Invalid'}
          </div>
        </div>
      </div>
      {/* User Object */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        <div className="bg-gray-900 px-4 py-2 border-b border-gray-700">
          <span className="text-sm font-mono text-gray-400">user (from AuthContext)</span>
        </div>
        <div className="p-4">
          {user ? (
            <div className="space-y-2 font-mono text-sm">
              {Object.entries(user)?.map(([key, value]) => (
                <div key={key} className="flex gap-4 py-1">
                  <span className="text-cyan-400 w-32 flex-shrink-0">{key}:</span>
                  <span className="flex-1 break-all">{renderValue(value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No user object</p>
          )}
        </div>
      </div>
      {/* UserProfile Object */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        <div className="bg-gray-900 px-4 py-2 border-b border-gray-700">
          <span className="text-sm font-mono text-gray-400">userProfile (from AuthContext)</span>
        </div>
        <div className="p-4">
          {userProfile ? (
            <div className="space-y-2 font-mono text-sm">
              {Object.entries(userProfile)?.map(([key, value]) => (
                <div key={key} className="flex gap-4 py-1">
                  <span className="text-cyan-400 w-32 flex-shrink-0">{key}:</span>
                  <span className="flex-1 break-all">{renderValue(value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No userProfile object</p>
          )}
        </div>
      </div>
      {/* Session Object */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        <div className="bg-gray-900 px-4 py-2 border-b border-gray-700">
          <span className="text-sm font-mono text-gray-400">session (from Supabase)</span>
        </div>
        <div className="p-4">
          {session ? (
            <div className="space-y-3">
              <div className="space-y-2 font-mono text-sm">
                <div className="flex gap-4 py-1">
                  <span className="text-cyan-400 w-32 flex-shrink-0">expires_at:</span>
                  <span className="flex-1 text-yellow-300">{formatExpiry(session?.expires_at)}</span>
                </div>
                <div className="flex gap-4 py-1">
                  <span className="text-cyan-400 w-32 flex-shrink-0">access_token:</span>
                  <span className="flex-1 text-gray-500">{session?.access_token ? '[REDACTED]' : 'null'}</span>
                </div>
                <div className="flex gap-4 py-1">
                  <span className="text-cyan-400 w-32 flex-shrink-0">refresh_token:</span>
                  <span className="flex-1 text-gray-500">{session?.refresh_token ? '[REDACTED]' : 'null'}</span>
                </div>
              </div>
              <details>
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                  View Full Session Object
                </summary>
                <pre className="text-xs text-gray-300 overflow-x-auto bg-gray-900 p-4 rounded mt-2">
                  {JSON.stringify(session, null, 2)}
                </pre>
              </details>
            </div>
          ) : (
            <p className="text-gray-500 italic">No session object</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthContextInspector;